import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Target, Clock, Check } from 'lucide-react';
import { ThemeId, themeById, SA, SA_SCALE, SA_VARIANTS, SaVariant, saItemsFor, prepareSa, saScore, saCategoryScores, saveThemeState, GOLD } from '../data/learning';
import { SaItem } from '../data/sa/types';
import { RadarChart } from '../components/ui/RadarChart';
import { CountUp } from '../components/ui/motion';
import { playDone } from '../utils/sound';

export const SelfScan: React.FC = () => {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const t = themeById((themeId as ThemeId) || 'ai');

  const [variant, setVariant] = useState<SaVariant | null>(null);
  const [items, setItems] = useState<SaItem[]>([]); // geschud per poging
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [done, setDone] = useState(false);

  const start = (v: SaVariant) => {
    const its = prepareSa(t.id, v);
    setItems(its); setVariant(v); setAnswers(its.map(() => null)); setIdx(0); setDone(false);
    window.scrollTo(0, 0);
  };
  const setAnswer = (val: number) => setAnswers((a) => a.map((x, i) => (i === idx ? val : x)));
  const last = idx === items.length - 1;
  const score = saScore(answers);

  const finish = () => { saveThemeState(t.id, { sa: score }); setDone(true); window.scrollTo(0, 0); playDone(); };

  // 1) Variant kiezen
  if (!variant) {
    return (
      <div className="shell py-12 sm:py-16 max-w-readable">
        <Link to={`/thema/${t.id}`} className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-ink mb-6">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> {t.name}
        </Link>
        <p className="label mb-3">Zelfscan · {t.name}</p>
        <h1 className="font-display font-bold text-ink mb-3" style={{ fontSize: 'clamp(28px,4vw,40px)', lineHeight: 1.1 }}>Waar sta je nu?</h1>
        <p className="text-sub mb-8" style={{ fontSize: 18 }}>Schat jezelf in op {SA[t.id].categories.length} onderdelen. Je krijgt een spinnenweb te zien met je score per onderdeel. Er is geen goed of fout — kies de variant die je past.</p>
        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
          {SA_VARIANTS.map((v) => {
            const n = saItemsFor(t.id, v.id).length;
            return (
              <button key={v.id} onClick={() => start(v.id)} className="card card-hover p-6 text-left flex flex-col">
                <span className="grid place-items-center rounded-tile mb-4" style={{ width: 44, height: 44, background: '#F6EFD9' }}>
                  {v.id === 'quick' ? <Clock style={{ color: GOLD }} strokeWidth={1.9} className="w-6 h-6" /> : <Target style={{ color: GOLD }} strokeWidth={1.9} className="w-6 h-6" />}
                </span>
                <h2 className="font-display font-semibold text-ink mb-1" style={{ fontSize: 21 }}>{v.label}</h2>
                <p className="text-sub mb-4 flex-1">{v.hint}</p>
                <p className="text-sm font-medium" style={{ color: '#8A6D12' }}>{n} vragen · {v.time}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // 3) Uitslag met spinnenweb
  if (done) {
    const cats = saCategoryScores(t.id, items, answers);
    const sterk = cats.filter((c) => c.value >= 4).map((c) => c.label);
    const aandacht = cats.filter((c) => c.value > 0 && c.value <= 2.5).map((c) => c.label);
    return (
      <div className="shell py-12 sm:py-16 max-w-readable">
        <p className="label mb-2 text-center">Je zelfscan · {t.name}</p>
        <h1 className="font-display font-bold text-ink mb-1 text-center anim-pop" style={{ fontSize: 'clamp(26px,4vw,38px)' }}>Je startniveau: <CountUp to={score} decimals={1} duration={1200} /></h1>
        <p className="text-sub mb-6 text-center" style={{ fontSize: 17 }}>Zo schat je jezelf in per onderdeel. Na de training maak je de toets en zie je je groei.</p>

        <div className="card p-5 sm:p-7 mb-6">
          <RadarChart data={cats} accent={GOLD} max={5} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-8">
          <div className="card p-5">
            <p className="label mb-3" style={{ color: '#2B7A4B' }}>Sterke punten</p>
            {sterk.length ? <ul className="flex flex-col gap-2">{sterk.map((s) => <li key={s} className="flex items-center gap-2 text-ink"><Check className="w-4 h-4 flex-none" style={{ color: '#2B7A4B' }} strokeWidth={2.2} /> {s}</li>)}</ul>
              : <p className="text-sub text-sm">Nog geen uitschieters omhoog — daar gaan de modules je bij helpen.</p>}
          </div>
          <div className="card p-5">
            <p className="label mb-3" style={{ color: '#8A2C3D' }}>Aandachtspunten</p>
            {aandacht.length ? <ul className="flex flex-col gap-2">{aandacht.map((s) => <li key={s} className="flex items-center gap-2 text-ink"><ArrowRight className="w-4 h-4 flex-none" style={{ color: '#8A2C3D' }} strokeWidth={2.2} /> {s}</li>)}</ul>
              : <p className="text-sub text-sm">Mooi — geen grote zwakke plekken. Verdiep je gerust verder.</p>}
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button onClick={() => navigate(`/thema/${t.id}`)} className="btn-gold">Start met leren <ArrowRight className="w-4 h-4" strokeWidth={1.9} /></button>
          <button onClick={() => setVariant(null)} className="btn-outline">Opnieuw</button>
        </div>
      </div>
    );
  }

  // 2) Vragen
  return (
    <div className="shell py-12 sm:py-16 max-w-readable">
      <button onClick={() => setVariant(null)} className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Andere variant
      </button>

      <div className="flex items-center gap-3 mb-7">
        <div className="flex-1"><span className="block w-full rounded-full bg-line overflow-hidden" style={{ height: 10 }}><span className="block h-full rounded-full" style={{ width: `${((idx + 1) / items.length) * 100}%`, background: GOLD, transition: 'width .3s' }} /></span></div>
        <span className="text-sm text-sub tabular-nums">Vraag {idx + 1} / {items.length}</span>
      </div>

      <p className="label mb-3" style={{ color: '#8A6D12' }}>{items[idx].category}</p>
      <h1 className="font-display font-bold text-ink mb-2" style={{ fontSize: 'clamp(24px,3.6vw,34px)', lineHeight: 1.2 }}>{items[idx].text}</h1>
      <p className="text-sub mb-7" style={{ fontSize: 18 }}>Hoe goed past deze uitspraak bij jou?</p>

      <div className="flex flex-col gap-3 mb-8">
        {SA_SCALE.map((label, v) => {
          const sel = answers[idx] === v + 1;
          return (
            <button key={v} onClick={() => setAnswer(v + 1)} className="flex items-center gap-3.5 text-left rounded-lg p-4 transition-colors border"
              style={{ borderColor: sel ? GOLD : '#E7E2DB', background: sel ? '#FBF4E2' : '#fff' }}>
              <span className="grid place-items-center rounded-full flex-none border-2" style={{ width: 24, height: 24, borderColor: sel ? GOLD : '#CFC5B2' }}>
                {sel && <span className="rounded-full" style={{ width: 12, height: 12, background: GOLD }} />}
              </span>
              <span className="text-ink font-medium" style={{ fontSize: 16.5 }}>{label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} className="btn-ghost" style={{ visibility: idx === 0 ? 'hidden' : 'visible' }}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Vorige
        </button>
        <button disabled={answers[idx] == null} onClick={() => (last ? finish() : setIdx((i) => i + 1))} className="btn-gold">
          {last ? 'Bekijk uitslag' : 'Volgende'} <ArrowRight className="w-4 h-4" strokeWidth={1.9} />
        </button>
      </div>
    </div>
  );
};
